import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArchiveIcon from "@mui/icons-material/Archive";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UpdateIcon from "@mui/icons-material/Update";
import { useNavigate } from "react-router-dom";
import { config } from "../config";

import { useCreatorWorkspaceApi } from "../hooks/useCreatorWorkspaceApi";
import { useEventsApi } from "../hooks/useEventsApi";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";
import type { CreatorSession, PendingApprovalEvent } from "../shared/workspace";
import type { EventAuditLogRow } from "../shared/eventAuditLog";
import EventsFormDialog from "../components/EventsFormDialog";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

const sessionStatusMeta: Record<string, { label: string; color: "default" | "primary" | "success" | "warning" }> = {
  in_progress: { label: "In progress", color: "primary" },
  submitted: { label: "Submitted", color: "success" },
  archived: { label: "Archived", color: "default" },
};

const sessionStatusIcon = (status: string) => {
  if (status === "in_progress") return <AccessTimeIcon fontSize="small" />;
  if (status === "submitted") return <CheckCircleIcon fontSize="small" />;
  if (status === "archived") return <ArchiveIcon fontSize="small" />;
  return undefined;
};

const sessionStatusLabel = (status: string): string =>
  sessionStatusMeta[status]?.label ?? status.replaceAll("_", " ");

const getActivityVisualMeta = (
  action: string
): { icon: React.ReactNode; accentColor: string; label: string } => {
  if (action === "create") {
    return {
      icon: <AddCircleOutlineIcon fontSize="small" />,
      accentColor: "success.main",
      label: "created",
    };
  }
  if (action === "delete") {
    return {
      icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
      accentColor: "error.main",
      label: "deleted",
    };
  }
  if (action === "update") {
    return {
      icon: <EditOutlinedIcon fontSize="small" />,
      accentColor: "warning.main",
      label: "modified",
    };
  }
  if (action === "approve") {
    return {
      icon: <CheckCircleIcon fontSize="small" />,
      accentColor: "success.main",
      label: "approved",
    };
  }
  return {
    icon: <HistoryIcon fontSize="small" />,
    accentColor: "info.main",
    label: "changed",
  };
};

const getSessionProgress = (session: CreatorSession): { percent: number; stepLabel: string } => {
  const payload = (session.payload ?? {}) as Record<string, any>;
  if (payload.step4SubmissionDone === true || session.status === "submitted") {
    return { percent: 100, stepLabel: "Completed" };
  }

  const eventCreation = String(payload.eventCreation ?? "none");
  const visibleStepIds =
    eventCreation === "rising"
      ? [0, 1, 2, 3, 5]
      : eventCreation === "resolving"
        ? [0, 1, 2, 4, 5]
        : [0, 1, 2, 3, 4, 5];

  const completedMap =
    payload.completed && typeof payload.completed === "object" ? payload.completed : {};
  const completedCount = visibleStepIds.filter((id) => Boolean(completedMap[String(id)] || completedMap[id])).length;
  const activeStep = Number(payload.activeStep ?? 0);
  const activeIndex = Math.max(0, visibleStepIds.indexOf(activeStep));
  const reachedCount = Math.max(completedCount, Math.min(visibleStepIds.length, activeIndex + 1));
  const percent = Math.max(0, Math.min(100, Math.round((reachedCount / visibleStepIds.length) * 100)));
  return { percent, stepLabel: `Step ${reachedCount}/${visibleStepIds.length}` };
};

export default function MyWorkspace() {
  const navigate = useNavigate();
  const { callApi } = useEventsApi();
  const {
    listMySessions,
    createSession,
    updateSession,
    deleteSession,
    getMyEventActivity,
    getMyPendingApprovals,
  } =
    useCreatorWorkspaceApi();

  const eventActivityPageSize = 10;
  const pendingPageSize = 10;
  const sessionPageSize = 10;
  const [sessions, setSessions] = React.useState<CreatorSession[]>([]);
  const [sessionOffset, setSessionOffset] = React.useState(0);
  const [sessionTotal, setSessionTotal] = React.useState(0);
  const [activity, setActivity] = React.useState<EventAuditLogRow[]>([]);
  const [activityOffset, setActivityOffset] = React.useState(0);
  const [activityTotal, setActivityTotal] = React.useState(0);
  const [pendingApprovals, setPendingApprovals] = React.useState<PendingApprovalEvent[]>([]);
  const [pendingOffset, setPendingOffset] = React.useState(0);
  const [pendingTotal, setPendingTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [sectionLoading, setSectionLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [pendingExpanded, setPendingExpanded] = React.useState(false);
  const [activityExpanded, setActivityExpanded] = React.useState(false);
  const [sessionsExpanded, setSessionsExpanded] = React.useState(false);
  const [dialogEventId, setDialogEventId] = React.useState<number | null>(null);
  const [dialogReadOnlyMode, setDialogReadOnlyMode] = React.useState<"none" | "recent_activity">("none");

  const openLatestByEventCode = React.useCallback(
    async (payload: { eventCode?: number | null; eventType?: string | null; eventContractName?: string | null }) => {
      const code = Number(payload.eventCode ?? 0);
      if (!Number.isFinite(code) || code <= 0) {
        setError("Could not resolve latest version: missing event code.");
        return;
      }
      try {
        const res = (await callApi(
          `${config.apiUrl}/api/events/?event_code_exact=${code}&latest=true&page_size=100`,
          "GET"
        )) as { results?: Array<{ global_event_id?: number; event_type?: string; event_contract_name?: string | null }> };
        const rows = res?.results ?? [];
        if (!rows.length) {
          setError(`Could not resolve latest version for event code ${code}.`);
          return;
        }

        const normalize = (v?: string | null) => (v ?? "").trim().toLowerCase();
        const wantedType = normalize(payload.eventType);
        const wantedContract = normalize(payload.eventContractName);

        const exact = rows.find(
          (r) => normalize(r.event_type) === wantedType && normalize(r.event_contract_name) === wantedContract
        );
        const typeOnly = rows.find((r) => normalize(r.event_type) === wantedType);
        const selected = exact ?? typeOnly ?? rows[0];
        const latestId = Number(selected?.global_event_id ?? 0);
        if (!latestId) {
          setError(`Could not resolve latest version for event code ${code}.`);
          return;
        }
        setDialogReadOnlyMode("none");
        setDialogEventId(latestId);
      } catch {
        setError(`Could not resolve latest version for event code ${code}.`);
      }
    },
    [callApi]
  );
  const theme = useTheme();
  const reloadAll = React.useCallback(async () => {
    try {
      setError(null);
      const [nextSessions, nextActivity, nextPending] = await Promise.all([
        listMySessions(sessionPageSize, sessionOffset),
        getMyEventActivity(eventActivityPageSize, activityOffset),
        getMyPendingApprovals(pendingPageSize, pendingOffset),
      ]);
      setSessions(nextSessions.results ?? []);
      setSessionTotal(nextSessions.total ?? 0);
      setActivity(nextActivity.results ?? []);
      setActivityTotal(nextActivity.total ?? 0);
      setPendingApprovals(nextPending.results ?? []);
      setPendingTotal(nextPending.total ?? 0);
    } catch {
      setError("Could not load workspace data.");
    } finally {
      setLoading(false);
    }
  }, [
    listMySessions,
    getMyEventActivity,
    getMyPendingApprovals,
    eventActivityPageSize,
    pendingPageSize,
    sessionPageSize,
    sessionOffset,
    activityOffset,
    pendingOffset,
  ]);

  React.useEffect(() => {
    void reloadAll();
  }, [reloadAll]);

  const reloadSections = React.useCallback(async () => {
    try {
      setSectionLoading(true);
      setError(null);
      const [nextActivity, nextPending] = await Promise.all([
        getMyEventActivity(eventActivityPageSize, activityOffset),
        getMyPendingApprovals(pendingPageSize, pendingOffset),
      ]);
      setActivity(nextActivity.results ?? []);
      setActivityTotal(nextActivity.total ?? 0);
      setPendingApprovals(nextPending.results ?? []);
      setPendingTotal(nextPending.total ?? 0);
    } catch {
      setError("Could not refresh workspace activity sections.");
    } finally {
      setSectionLoading(false);
    }
  }, [
    getMyEventActivity,
    getMyPendingApprovals,
    eventActivityPageSize,
    pendingPageSize,
    activityOffset,
    pendingOffset,
  ]);

  React.useEffect(() => {
    if (loading) return;
    void reloadSections();
  }, [activityOffset, pendingOffset]);

  const handleCreateSession = async () => {
    try {
      setCreating(true);
      const now = new Date();
      const title = `Draft ${now.toISOString().slice(0, 16).replace("T", " ")}`;
      const created = await createSession({ title, status: "in_progress", payload: {} });
      navigate("/event-configurator", { state: { workspaceSessionId: created.id } });
    } catch {
      setError("Could not create a new session.");
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (session: CreatorSession) => {
    const next = window.prompt("Rename session", session.title);
    if (!next || next.trim() === "" || next.trim() === session.title) return;
    try {
      await updateSession(session.id, { title: next.trim() });
      await reloadAll();
    } catch {
      setError("Could not rename session.");
    }
  };

  const handleDelete = async (session: CreatorSession) => {
    const ok = window.confirm(`Delete session "${session.title}"?`);
    if (!ok) return;
    try {
      await deleteSession(session.id);
      if (sessions.length === 1 && sessionOffset > 0) {
        setSessionOffset((prev) => Math.max(0, prev - sessionPageSize));
      }
      await reloadAll();
    } catch {
      setError("Could not delete session.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      <Box>
        <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>My Workspace</Typography>
        <Typography variant="body2" color="text.secondary">
          Continue saved creator sessions and check your latest activity.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Accordion
          disableGutters
          expanded={sessionsExpanded}
          onChange={(_, expanded) => setSessionsExpanded(expanded)}
          sx={{
            boxShadow: "none",
            "&::before": { display: "none" },
            backgroundColor: "transparent",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "primary.50" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SaveOutlinedIcon fontSize="small" color="primary" />
              <Typography variant="h6">My Saved Creator Sessions</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {sessionTotal === 0 ? 0 : sessionOffset + 1}-
                {Math.min(sessionOffset + sessions.length, sessionTotal)} of {sessionTotal}
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                disabled={creating}
                onClick={() => void handleCreateSession()}
              >
                New Session
              </Button>
            </Stack>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<NavigateBeforeIcon />}
                    disabled={sessionOffset === 0 || sectionLoading}
                    onClick={() => setSessionOffset((prev) => Math.max(0, prev - sessionPageSize))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    disabled={sessionOffset + sessionPageSize >= sessionTotal || sectionLoading}
                    onClick={() => setSessionOffset((prev) => prev + sessionPageSize)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              {sessions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No saved sessions yet.
                </Typography>
              )}
              {sessions.map((session) => {
                const progress = getSessionProgress(session);
                return (
                  <Box
                    key={session.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      backgroundColor: "action.hover",
                      borderLeft: "4px solid",
                      borderLeftColor: "primary.main",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={600}>{session.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Updated {formatLocalDateTime(session.updated_at)}
                        </Typography>
                        <Box sx={{ mt: 1.25, minWidth: 220 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progress.percent}
                            sx={{ height: 8, borderRadius: 5 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {progress.stepLabel} ({progress.percent}%)
                          </Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          color={sessionStatusMeta[session.status]?.color ?? "default"}
                          label={sessionStatusLabel(session.status)}
                          icon={sessionStatusIcon(session.status)}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlayArrowIcon />}
                          onClick={() =>
                            navigate("/event-configurator", { state: { workspaceSessionId: session.id } })
                          }
                        >
                          Continue
                        </Button>
                        <IconButton size="small" onClick={() => void handleRename(session)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => void handleDelete(session)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Accordion
          disableGutters
          expanded={pendingExpanded}
          onChange={(_, expanded) => setPendingExpanded(expanded)}
          sx={{
            boxShadow: "none",
            "&::before": { display: "none" },
            backgroundColor: "transparent",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "warning.50" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PendingActionsIcon fontSize="small" color="warning" />
              <Typography variant="h6">My Events Awaiting Approval</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Showing {pendingTotal === 0 ? 0 : pendingOffset + 1}-
                  {Math.min(pendingOffset + pendingApprovals.length, pendingTotal)} of {pendingTotal}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<NavigateBeforeIcon />}
                    disabled={pendingOffset === 0 || sectionLoading}
                    onClick={() => setPendingOffset((prev) => Math.max(0, prev - pendingPageSize))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    disabled={pendingOffset + pendingPageSize >= pendingTotal || sectionLoading}
                    onClick={() => setPendingOffset((prev) => prev + pendingPageSize)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              {pendingApprovals.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No events created or modified by you are currently awaiting approval.
                </Typography>
              )}
              {pendingApprovals.map((item) => (
                <Box
                  key={item.global_event_id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    backgroundColor: "action.hover",
                    borderLeft: "4px solid",
                    borderLeftColor: "warning.main",
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      Event {item.event_code ?? "?"} ({item.event_type})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.event_contract_name || "No contract name"} - v{item.version ?? 0}
                    </Typography>
                    {item.event_hmi_label_eng ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.event_hmi_label_eng}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      Last change by you: {item.latest_action} at{" "}
                      {item.latest_action_at ? formatLocalDateTime(item.latest_action_at) : "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Awaiting approval by an approver/admin user.
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UpdateIcon />}
                        onClick={() =>
                          void openLatestByEventCode({
                            eventCode: item.event_code,
                            eventType: item.event_type,
                            eventContractName: item.event_contract_name,
                          })
                        }
                      >
                        Open latest version
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Accordion
          disableGutters
          expanded={activityExpanded}
          onChange={(_, expanded) => setActivityExpanded(expanded)}
          sx={{
            boxShadow: "none",
            "&::before": { display: "none" },
            backgroundColor: "transparent",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "info.50" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon fontSize="small" color="info" />
              <Typography variant="h6">My Recent Activity</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Showing {activityTotal === 0 ? 0 : activityOffset + 1}-
                  {Math.min(activityOffset + activity.length, activityTotal)} of {activityTotal}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<NavigateBeforeIcon />}
                    disabled={activityOffset === 0 || sectionLoading}
                    onClick={() => setActivityOffset((prev) => Math.max(0, prev - eventActivityPageSize))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    disabled={activityOffset + eventActivityPageSize >= activityTotal || sectionLoading}
                    onClick={() => setActivityOffset((prev) => prev + eventActivityPageSize)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              {activity.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No activity yet.
                </Typography>
              )}
              {activity.map((entry) => (
                (() => {
                  const actionMeta = getActivityVisualMeta(entry.action);
                  return (
                    <Box
                      key={entry.id}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        backgroundColor: "action.hover",
                        borderLeft: "4px solid",
                        borderLeftColor: actionMeta.accentColor,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: actionMeta.accentColor }}>
                        {actionMeta.icon}
                        <Typography variant="body2" fontWeight={600} sx={{ color: "inherit" }}>
                          Event {entry.event_code ?? "?"} ({entry.event_type || "unknown type"}) {actionMeta.label}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatLocalDateTime(entry.created_at)}
                        {entry.event_contract_name ? ` - ${entry.event_contract_name}` : ""}
                      </Typography>
                      {entry.event_hmi_label_eng ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {entry.event_hmi_label_eng}
                        </Typography>
                      ) : null}
                      {entry.global_event_id ? (
                        <Box sx={{ mt: 0.75 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {entry.action !== "delete" && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<HistoryIcon />}
                                onClick={() => {
                                  setDialogReadOnlyMode("recent_activity");
                                  setDialogEventId(entry.global_event_id);
                                }}
                              >
                                Open historical version
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<UpdateIcon />}
                              onClick={() =>
                                void openLatestByEventCode({
                                  eventCode: entry.event_code,
                                  eventType: entry.event_type,
                                  eventContractName: entry.event_contract_name,
                                })
                              }
                            >
                              Open latest version
                            </Button>
                          </Stack>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })()
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>
      <EventsFormDialog
        open={dialogEventId != null}
        eventId={dialogEventId}
        createNewEvent={false}
        forceReadOnly={dialogReadOnlyMode === "recent_activity"}
        readOnlyHint={
          dialogReadOnlyMode === "recent_activity"
            ? "Recent Activity mode: this opens the selected historical event version in read-only mode. Use the latest event entry for edits."
            : undefined
        }
        onClose={() => {
          setDialogEventId(null);
          setDialogReadOnlyMode("none");
        }}
        onSaved={() => {
          void reloadSections();
        }}
      />
    </Stack>
  );
}
